/**
 * Analyzer: Day Duration Profile
 *
 * Observations:
 *   - "Holding losers too long" when avgLossDuration > 1.5× avgWinDuration
 *   - "Cut winners short / let losers run" inverse hint
 *   - Scalping-vs-patience edge: compares ≤5min trades vs >5min
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types';

export const key = 'dayDurationProfile';
export const tier = 'free';

export const analyze = (ctx) => {
  const { day } = ctx;
  if (!day || !day.time || !day.time.duration) return null;

  const d = day.time.duration;
  const out = [];

  // Holding losers too long
  if (d.avgLossDuration > d.avgWinDuration * 1.5 && d.avgLossDuration > 0 && d.avgWinDuration > 0) {
    out.push(makeObservation({
      id: `${key}:${day.date}:loser-drag`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.WARNING,
      category: CATEGORY.RISK,
      title: 'Holding losers too long',
      detail: `Avg losing trade ${d.avgLossDuration.toFixed(0)}min vs winning ${d.avgWinDuration.toFixed(0)}min. Hope is bleeding into size.`,
      action: 'Pick a max-hold-when-red rule (e.g. 10min) and enforce it.',
      evidence: { avgWinDuration: d.avgWinDuration, avgLossDuration: d.avgLossDuration },
      confidence: 0.85,
      priority: 78
    }));
  } else if (d.avgWinDuration > d.avgLossDuration * 2 && d.avgWinDuration > 0 && d.avgLossDuration > 0) {
    // Good trade management — let winners run
    out.push(makeObservation({
      id: `${key}:${day.date}:runners`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.RISK,
      title: 'Good trade management',
      detail: `Winners ran ${d.avgWinDuration.toFixed(0)}min avg; losers cut at ${d.avgLossDuration.toFixed(0)}min avg.`,
      action: 'Keep the asymmetry — losers out fast, winners get room. Note which exits triggered today.',
      evidence: { avgWinDuration: d.avgWinDuration, avgLossDuration: d.avgLossDuration },
      confidence: 0.8,
      priority: 55
    }));
  }

  // Scalping vs patience edge
  if (d.quick.count >= 2 && d.longer.count >= 2) {
    if (d.quick.pnl > d.longer.pnl && d.quick.winRate > d.longer.winRate) {
      out.push(makeObservation({
        id: `${key}:${day.date}:scalp-edge`,
        analyzer: key,
        scope: 'day',
        scopeId: day.date,
        severity: SEVERITY.INFO,
        category: CATEGORY.SETUP,
        title: 'Scalping edge today',
        detail: `Quick trades (≤5m): $${d.quick.pnl.toFixed(0)} @ ${d.quick.winRate.toFixed(0)}% vs longer: $${d.longer.pnl.toFixed(0)} @ ${d.longer.winRate.toFixed(0)}%.`,
        action: 'Regime favored quick cuts today. Trust the exit speed; don\'t force swing holds when tape is choppy.',
        evidence: { quick: d.quick, longer: d.longer },
        confidence: 0.7,
        priority: 45
      }));
    } else if (d.longer.pnl > d.quick.pnl && d.longer.winRate > d.quick.winRate) {
      out.push(makeObservation({
        id: `${key}:${day.date}:patience-edge`,
        analyzer: key,
        scope: 'day',
        scopeId: day.date,
        severity: SEVERITY.INFO,
        category: CATEGORY.SETUP,
        title: 'Patience paid today',
        detail: `Longer holds (>5m): $${d.longer.pnl.toFixed(0)} @ ${d.longer.winRate.toFixed(0)}% vs quick: $${d.quick.pnl.toFixed(0)} @ ${d.quick.winRate.toFixed(0)}%.`,
        action: 'Resist the early-exit urge when thesis is intact.',
        evidence: { quick: d.quick, longer: d.longer },
        confidence: 0.7,
        priority: 48
      }));
    }
  }

  return out.length > 0 ? out : null;
};
